import type {
  FullStatsDto,
  PreflopOrMatrix,
  PreflopOrStatId,
  ThreeBetVsMatrix,
} from '@poker-stats/contracts';

const PREFLOP_POSITIONS = ['tot', 'ep', 'mp', 'co', 'btn', 'sb', 'bb'] as const;
const THREEBET_HERO_KEYS = ['ep', 'mp', 'co', 'btn', 'sb', 'bb'] as const;
const THREEBET_VILLAIN_KEYS = ['tot', 'ep', 'mp', 'co', 'btn', 'sb'] as const;

const PREFLOP_OR_STAT_IDS: PreflopOrStatId[] = [
  'openraise',
  'foldtothreebet',
  'foldtothreebetip',
  'foldtothreebetoop',
  'fourbet',
  'fourbetip',
  'fourbetoop',
  'callthreebet',
  'callthreebetip',
  'callthreebetoop',
  'fourbr',
  'fourbrip',
  'fourbroop',
  'foldtofivebet',
  'foldtofivebetip',
  'foldtofivebetoop',
  'c3br',
  'c3brip',
  'c3broop',
  'isoraisev1',
  'isoraisev1ip',
  'isoraisev1oop',
  'isoraisev2',
  'isoraisev3',
  'limp',
  'limpfold',
  'limpraise',
];

let seedCounter = 1;

function nextSeed() {
  seedCounter += 1;
  return seedCounter;
}

/** Deterministic demo value — stable across reloads, varied across cells. */
function demoPct(seed = nextSeed()) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return 5 + Math.floor((x - Math.floor(x)) * 68);
}

function demoSample(seed = nextSeed()) {
  return demoPct(seed) * 4 + 20;
}

function streetTriple(prefix: string, seed: number, withPossible = true): Record<string, number> {
  const values = {
    [`${prefix}flop`]: demoPct(seed),
    [`${prefix}turn`]: demoPct(seed + 1),
    [`${prefix}river`]: demoPct(seed + 2),
  };

  if (!withPossible) {
    return values;
  }

  return {
    ...values,
    [`${prefix}floppossible`]: demoSample(seed + 3),
    [`${prefix}turnpossible`]: demoSample(seed + 4),
    [`${prefix}riverpossible`]: demoSample(seed + 5),
  };
}

function buildPreflopOrMatrix(baseSeed: number): PreflopOrMatrix {
  return Object.fromEntries(
    PREFLOP_POSITIONS.map((position, positionIndex) => [
      position,
      Object.fromEntries(
        PREFLOP_OR_STAT_IDS.map((statId, statIndex) => {
          const seed = baseSeed + positionIndex * 100 + statIndex;
          return [statId, { value: demoPct(seed), sample: demoSample(seed + 17) }];
        }),
      ),
    ]),
  ) as PreflopOrMatrix;
}

function buildThreeBetMatrix(baseSeed: number): ThreeBetVsMatrix {
  return Object.fromEntries(
    THREEBET_HERO_KEYS.map((hero, heroIndex) => [
      hero,
      Object.fromEntries(
        THREEBET_VILLAIN_KEYS.map((villain, villainIndex) => {
          const seed = baseSeed + heroIndex * 20 + villainIndex;
          return [villain, { value: demoPct(seed), sample: demoSample(seed + 11) }];
        }),
      ),
    ]),
  ) as unknown as ThreeBetVsMatrix;
}

function buildScalarStats(): Partial<FullStatsDto> {
  return {
    hands: 10_000,
    vpip: 26,
    pfr: 19,
    winnings: 0,
    bb100: 0,
    evWinnings: 0,
    evBb100: 0,
    rlBb100: 0,
    wonwhensawflop: 45,
    threebet: 9,
    foldedtothreebetpreflop: 48,
    raisedthreebetpreflop: 13,
    foldedtofourbetpreflop: 47,
    raisedfourbetpreflop: 20,
    squeeze: 9,
    coldcall: 15,
    calledthreebetpreflop: 39,
    sawshowdown: 26,
    wonshowdown: 49,
    smallblindresteal: 12,
    bigblindresteal: 11,
    ...streetTriple('cbet', 100),
    foldflopcbet: demoPct(110),
    foldturncbet: demoPct(111),
    foldrivercbet: demoPct(112),
    facingflopcbet: demoSample(113),
    facingturncbet: demoSample(114),
    facingrivercbet: demoSample(115),
    raisedflopcontinuationbet: demoPct(120),
    raisedturncontinuationbet: demoPct(121),
    raisedrivercontinuationbet: demoPct(122),
    flopbet: demoPct(130),
    turnbet: demoPct(131),
    riverbet: demoPct(132),
    flopbetpossible: demoSample(133),
    turnbetpossible: demoSample(134),
    riverbetpossible: demoSample(135),
    betfoldflop: demoPct(140),
    betfoldturn: demoPct(141),
    betfoldriver: demoPct(142),
    betfoldfloppossible: demoSample(143),
    betfoldturnpossible: demoSample(144),
    betfoldriverpossible: demoSample(145),
    flopfold: demoPct(150),
    turnfold: demoPct(151),
    riverfold: demoPct(152),
    flopfoldpossible: demoSample(153),
    turnfoldpossible: demoSample(154),
    riverfoldpossible: demoSample(155),
    flopraise: demoPct(160),
    turnraise: demoPct(161),
    riverraise: demoPct(162),
    flopraisepossible: demoSample(163),
    turnraisepossible: demoSample(164),
    riverraisepossible: demoSample(165),
    flopwafterr: demoPct(170),
    turnwafterr: demoPct(171),
    riverwafterr: demoPct(172),
    flopwafterrpossible: demoSample(173),
    turnwafterrpossible: demoSample(174),
    riverwafterrpossible: demoSample(175),
    ...streetTriple('donk', 180),
    ...streetTriple('xr', 190),
    flopagrfactor: demoPct(200),
    turnagrfactor: demoPct(201),
    riveragrfactor: demoPct(202),
    flopagfpossible: demoSample(203),
    turnagfpossible: demoSample(204),
    riveragfpossible: demoSample(205),
    ...streetTriple('donk3betp', 210),
    foldvsdonkflop: demoPct(220),
    foldvsdonkturn: demoPct(221),
    foldvsdonkriver: demoPct(222),
    facingflopdonk: demoSample(223),
    facingturndonk: demoSample(224),
    facingriverdonk: demoSample(225),
    ...streetTriple('donkfold', 230),
    cbetflopip: demoPct(240),
    cbetturnip: demoPct(241),
    cbetriverip: demoPct(242),
    cbetflopippossible: demoSample(243),
    cbetturnippossible: demoSample(244),
    cbetriverippossible: demoSample(245),
    cbetflopoop: demoPct(250),
    cbetturnoop: demoPct(251),
    cbetriveroop: demoPct(252),
    cbetflopooppossible: demoSample(253),
    cbetturnooppossible: demoSample(254),
    cbetriverooppossible: demoSample(255),
    x_fflop: demoPct(260),
    x_ffturn: demoPct(261),
    x_ffriver: demoPct(262),
    x_ffloppossible: demoSample(263),
    x_ffturnpossible: demoSample(264),
    x_ffriverpossible: demoSample(265),
    foldflopcbetip: demoPct(270),
    foldturncbetip: demoPct(271),
    foldrivercbetip: demoPct(272),
    foldflopcbetippossible: demoSample(273),
    foldturncbetippossible: demoSample(274),
    foldrivercbetippossible: demoSample(275),
    foldflopcbetoop: demoPct(280),
    foldturncbetoop: demoPct(281),
    foldrivercbetoop: demoPct(282),
    foldflopcbetooppossible: demoSample(283),
    foldturncbetooppossible: demoSample(284),
    foldrivercbetooppossible: demoSample(285),
    raisedflopcontinuationbetip: demoPct(290),
    raisedturncontinuationbetip: demoPct(291),
    raisedrivercontinuationbetip: demoPct(292),
    raisedflopcontinuationbetippossible: demoSample(293),
    raisedturncontinuationbetippossible: demoSample(294),
    raisedrivercontinuationbetippossible: demoSample(295),
    raisedflopcontinuationbetoop: demoPct(300),
    raisedturncontinuationbetoop: demoPct(301),
    raisedrivercontinuationbetoop: demoPct(302),
    raisedflopcontinuationbetooppossible: demoSample(303),
    raisedturncontinuationbetooppossible: demoSample(304),
    raisedrivercontinuationbetooppossible: demoSample(305),
    cbfoldraise: demoPct(310),
    cbfoldraisepossible: demoSample(311),
    cbfoldraiseip: demoPct(312),
    cbfoldraiseippossible: demoSample(313),
    cbfoldraiseoop: demoPct(314),
    cbfoldraiseooppossible: demoSample(315),
    cbcallfoldturn: demoPct(316),
    cbcallfoldturnpossible: demoSample(317),
    cbcallcallfoldriver: demoPct(318),
    cbcallcallfoldriverpossible: demoSample(319),
    bxbip: demoPct(320),
    bxbippossible: demoSample(321),
    bxfip: demoPct(322),
    bxfippossible: demoSample(323),
    bxboop: demoPct(324),
    bxbooppossible: demoSample(325),
    bxfoop: demoPct(326),
    bxfooppossible: demoSample(327),
    bxbtot: demoPct(328),
    bxbtotpossible: demoSample(329),
    bxftot: demoPct(330),
    bxftotpossible: demoSample(331),
    bcallfoldturn: demoPct(332),
    bcallfoldturnpossible: demoSample(333),
    bcallcallfoldriver: demoPct(334),
    bcallcallfoldriverpossible: demoSample(335),
    rfaftercallflopturnfold: demoPct(336),
    rfaftercallflopturnfoldpossible: demoSample(337),
    rfaftercallturnfold: demoPct(338),
    rfaftercallturnfoldpossible: demoSample(339),
  };
}

/** Demo stats for the FAQ decoding panel — illustrative values only. */
export const FAQ_STATS_PREVIEW: Partial<FullStatsDto> = {
  ...buildScalarStats(),
  preflopOr: buildPreflopOrMatrix(3000),
  threeBetVs: buildThreeBetMatrix(4000),
  foldFourBetVs: buildThreeBetMatrix(5000),
  fiveBetVs: buildThreeBetMatrix(6000),
  coldCallVs: buildThreeBetMatrix(7000),
  squeeze1cVs: buildThreeBetMatrix(8000),
};

export const FAQ_PREFLOP_PREVIEW: Array<[string, string | number]> = [
  ['3bet', FAQ_STATS_PREVIEW.threebet ?? '—'],
  ['F3bet', FAQ_STATS_PREVIEW.foldedtothreebetpreflop ?? '—'],
  ['4bet', FAQ_STATS_PREVIEW.raisedthreebetpreflop ?? '—'],
  ['F4bet', FAQ_STATS_PREVIEW.foldedtofourbetpreflop ?? '—'],
  ['5bet', FAQ_STATS_PREVIEW.raisedfourbetpreflop ?? '—'],
  ['SQZ', FAQ_STATS_PREVIEW.squeeze ?? '—'],
  ['CC', FAQ_STATS_PREVIEW.coldcall ?? '—'],
  ['WTSD', FAQ_STATS_PREVIEW.sawshowdown ?? '—'],
  ['WWSF', FAQ_STATS_PREVIEW.wonwhensawflop ?? '—'],
  ['W$SD', FAQ_STATS_PREVIEW.wonshowdown ?? '—'],
];
